import { useState, useEffect, useCallback } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

export function useMids({
  filters = {},
  page = 1,
  limit = 25,
  sort = "-createdAt",
} = {}) {
  const [mids, setMids] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  // SOUND-295: aggregate counts over the FILTERED set, computed server-side.
  // KPI cards must never be derived from the current page.
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  // Shared by the paged fetch and the export so a CSV can never drift from the
  // table it was taken from — one place decides what the filters mean.
  const buildParams = useCallback(
    (overrides = {}) => {
      const params = new URLSearchParams();
      const sortField = sort.replace(/^-/, "");
      params.set("sortBy", sortField);
      params.set("sortDir", sort.startsWith("-") ? "desc" : "asc");
      if (filters.search) params.set("search", filters.search);
      if (filters.status) params.set("status", filters.status);
      if (filters.processorName) params.set("processorName", filters.processorName);
      if (filters.source) params.set("source", filters.source);
      if (filters.companyId) params.set("companyId", filters.companyId);
      if (filters.dbaName) params.set("dbaName", filters.dbaName);
      Object.entries(overrides).forEach(([k, v]) => params.set(k, String(v)));
      return params;
    },
    [
      sort,
      filters.search,
      filters.status,
      filters.processorName,
      filters.source,
      filters.companyId,
      filters.dbaName,
    ]
  );

  /**
   * Every MID matching the CURRENT filters, not just the current page.
   *
   * The endpoint caps `limit` at 100, so this pages. It stops on a short page or
   * when it has the reported total, and hard-stops at MAX_PAGES so a bad total
   * can never spin forever. Returns { rows, truncated } — the caller is expected
   * to tell the user when an export is incomplete rather than silently hand over
   * a partial file.
   */
  const fetchAll = useCallback(async () => {
    if (!companyId) return { rows: [], truncated: false };
    const PAGE = 100;
    const MAX_PAGES = 50; // 5,000 rows
    const authUser = { id: auth?.user?.id, accessToken: auth?.user?.accessToken };
    const rows = [];
    let pageNum = 1;
    let total = null;

    while (pageNum <= MAX_PAGES) {
      const params = buildParams({ page: pageNum, limit: PAGE });
      const response = await makeApiRequest(
        "get",
        `${apiRoutes.listMids}?${params.toString()}`,
        authUser,
        companyId
      );
      const body = response?.data || {};
      const batch = Array.isArray(body.data) ? body.data : [];
      rows.push(...batch);
      total = body.pagination?.total ?? total;

      if (batch.length < PAGE) break;
      if (total !== null && rows.length >= total) break;
      pageNum += 1;
    }

    return { rows, truncated: total !== null && rows.length < total };
  }, [companyId, auth?.user?.id, auth?.user?.accessToken, buildParams]);

  const fetchMids = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      // MIDs endpoint uses sortBy/sortDir instead of sort — handled in buildParams.
      const params = buildParams({ page, limit });

      const response = await makeApiRequest(
        "get",
        `${apiRoutes.listMids}?${params.toString()}`,
        { id: auth?.user?.id, accessToken: auth?.user?.accessToken },
        companyId
      );

      const body = response?.data || {};
      setMids(Array.isArray(body.data) ? body.data : []);
      setPagination(body.pagination || { page: 1, limit: 25, total: 0, totalPages: 0 });
      setSummary(body.summary || {});
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [companyId, page, limit, buildParams, auth?.user?.id, auth?.user?.accessToken]);

  useEffect(() => {
    fetchMids();
  }, [fetchMids]);

  return { mids, pagination, summary, loading, error, refetch: fetchMids, fetchAll };
}
